import { useEffect, useRef } from "react";

interface FocusModeArgs {
  leftNavCollapsed: boolean;
  setLeftNavCollapsed: (next: boolean) => void;
  relatedCollapsed: boolean;
  setRelatedCollapsed: (next: boolean) => void;
  chatCollapsed: boolean;
  setChatCollapsed: (next: boolean) => void;
}

function isTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

// Spec: manna-web-ui §3.5 + shortcut table — `f` toggles Focus mode
// (collapses LeftNav + Related + Chat rails). Pressing `f` again restores
// the rails to whatever state they had before Focus mode engaged.
export function useFocusModeShortcut({
  leftNavCollapsed,
  setLeftNavCollapsed,
  relatedCollapsed,
  setRelatedCollapsed,
  chatCollapsed,
  setChatCollapsed,
}: FocusModeArgs) {
  const restoreRef = useRef<{ leftNav: boolean; related: boolean; chat: boolean } | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "f" && e.key !== "F") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTextInput(e.target)) return;
      e.preventDefault();

      if (restoreRef.current === null) {
        restoreRef.current = {
          leftNav: leftNavCollapsed,
          related: relatedCollapsed,
          chat: chatCollapsed,
        };
        if (!leftNavCollapsed) setLeftNavCollapsed(true);
        if (!relatedCollapsed) setRelatedCollapsed(true);
        if (!chatCollapsed) setChatCollapsed(true);
      } else {
        const snap = restoreRef.current;
        restoreRef.current = null;
        if (leftNavCollapsed !== snap.leftNav) setLeftNavCollapsed(snap.leftNav);
        if (relatedCollapsed !== snap.related) setRelatedCollapsed(snap.related);
        if (chatCollapsed !== snap.chat) setChatCollapsed(snap.chat);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    leftNavCollapsed,
    setLeftNavCollapsed,
    relatedCollapsed,
    setRelatedCollapsed,
    chatCollapsed,
    setChatCollapsed,
  ]);
}
